export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              With project starts in the Lower Mainland often hitting delays due to financing or permitting, the last thing a company needs is a large payroll sitting idle. On the flip side, when three projects greenlight at once, your in-house team is instantly underwater.
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-bar-chart-grouped-line text-white text-xl"></i>
                </span>
                The Volatility Problem
              </h2>
              <p className="text-gray-700 mb-4">
                Vancouver's construction market in 2026 is characterized by extreme unpredictability:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Permit Delays:</strong> City of Vancouver processing times can stretch 6-12 months</li>
                <li><strong>Financing Gaps:</strong> Interest rate fluctuations cause sudden project postponements</li>
                <li><strong>Weather Windows:</strong> BC's rainy season creates compressed construction timelines</li>
                <li><strong>Labor Shortages:</strong> Skilled trades availability varies dramatically month-to-month</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                In this environment, maintaining a fixed-size in-house detailing team means you're either <strong>overstaffed during slow periods</strong> or <strong>overwhelmed during busy periods</strong>. Neither scenario is profitable.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-flashlight-line text-white text-xl"></i>
                </span>
                The Solution: Dinari Designs as Your Surge Capacity
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Dinari Designs</strong> provides "Surge Capacity"—we act as your on-call detailing department. Here's how it works:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Instant Scalability:</strong> When three projects greenlight simultaneously, we absorb the overflow</li>
                <li><strong>Zero Ramp-Up Time:</strong> No hiring, training, or onboarding delays</li>
                <li><strong>Flexible Engagement:</strong> Use us for one project or ten—whatever your pipeline demands</li>
                <li><strong>Geographic Coverage:</strong> From high-rises in Burnaby to custom builds in North Vancouver</li>
                <li><strong>Predictable Costs:</strong> Fixed project pricing means no surprise overtime or benefits costs</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                We scale with you during busy periods, then scale back when you're done. Your overhead adjusts automatically to match your actual workload.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-briefcase-line text-white text-xl"></i>
                </span>
                Real-World Scenario: The Three-Project Surge
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>The Challenge:</strong> A mid-size Vancouver contractor has one in-house detailer handling their typical workload of 1-2 active projects. Suddenly, three new projects receive permits in the same week:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>A 6-story residential building in Richmond</li>
                <li>A townhouse complex in Coquitlam</li>
                <li>A custom home renovation in West Vancouver</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-4">
                <strong>Traditional Approach:</strong> Hire 2-3 additional detailers (3-6 months recruitment + training), or turn down projects.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Dinari Designs Approach:</strong> We handle the overflow immediately. Your in-house person focuses on the Richmond high-rise, while we deliver complete layout packages for the Coquitlam and West Vancouver projects within your timeline.
              </p>
              <p className="text-gray-700 mb-0">
                <strong>Result:</strong> You take on all three projects, maximize revenue, and avoid long-term hiring commitments.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-shield-star-line text-white text-xl"></i>
                </span>
                The Competitive Advantage
              </h2>
              <p className="text-gray-700 mb-0">
                In 2026's competitive Lower Mainland market, the ability to say "yes" to new projects is a massive advantage. While your competitors are turning down work due to capacity constraints, you're growing your business. <strong>Dinari Designs</strong> allows you to take on more work without the risk of long-term hiring—giving you the agility to dominate your market segment.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Whether you're tackling a high-rise in Burnaby or a custom build in North Vancouver, <strong>Dinari Designs</strong> provides the surge capacity you need to say "yes" to every opportunity.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
